import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { useConsumerCart } from './useConsumerCart'
import {
  CartProductError,
  ConsumerCart,
  DiscountError,
  DiscountWarning,
  InvalidTimeslotPrepTimeWarning,
  InvalidTimeslotWarning,
  MinimumValueWarning,
  OrderItemWithLimit,
  OriginalFulfillmentAvailableWarning
} from 'shop/types/cart'
import { useShop } from './useGlobalContext'
import { trackAddShippingInfoConsumerCart } from 'shop/components/Checkout/tracking/helpers'
import { CurrentLocationState, PartnerStore } from 'shop/components'
import {
  findErrorWarning,
  isCartProductError
} from 'shop/components/Cart/utils'
import { FulfillmentType, ORDER_AT_TABLE_FULFILLMENT } from 'shop/types'
import { findDefaultFulfillment } from 'shop/utils/common'
import { createDefaultCartParams } from 'shop/utils'
import { useNavigate } from 'react-router-dom'
import {
  getCurrentDiscountWarningErrors,
  isCartErrorCode,
  isInvalidTimeSlotPrepTimeWarning,
  isOriginalFulfillmentAvailableWarning,
  isTimeSlotWarning,
  trackTimeslotEvent
} from 'shop/components/Checkout/utils'
import { useQueryParams } from './useQueryParams'
import { TrackableEvent } from 'tracker/types'
import { useSessionStorage } from './useSessionStorage'

interface CheckoutState {
  giftWrapped: boolean
  setGiftWrapped: (value: boolean) => void
  giftWrapMessage: string | null
  setGiftWrapMessage: (message: string | null) => void
  trackAddShippingInfo: () => void
  updateCartGiftWrapping: (message: string | null) => void
  timeSlotValidation: InvalidTimeslotWarning | null
  dynamicPrepTimeSlotValidation: InvalidTimeslotPrepTimeWarning | null
  setDynamicPrepTimeSlotValidation: React.Dispatch<
    React.SetStateAction<InvalidTimeslotPrepTimeWarning | null>
  >
  productError: CartProductError | null
  discountErrorWarnings: (DiscountWarning | DiscountError)[]
  minimumOrderValueWarning: MinimumValueWarning | null
  rewardsRef: React.RefObject<HTMLDivElement>
  isPaying: boolean
  setIsPaying: React.Dispatch<React.SetStateAction<boolean>>
  isTippingOpen: boolean
  setIsTippingOpen: React.Dispatch<React.SetStateAction<boolean>>
  openTippingModal: () => void
  createDefaultCart: (
    currentPartnerStore: PartnerStore,
    queryParamFulfillmentType: FulfillmentType
  ) => Promise<ConsumerCart | void>
  updateExceededLimitItems: (orderItems: OrderItemWithLimit[]) => Promise<void>
}

const CheckoutContext = createContext<CheckoutState>({
  giftWrapped: false,
  setGiftWrapped: () => null,
  giftWrapMessage: null,
  setGiftWrapMessage: () => null,
  trackAddShippingInfo: () => null,
  updateCartGiftWrapping: () => null,
  timeSlotValidation: null,
  dynamicPrepTimeSlotValidation: null,
  setDynamicPrepTimeSlotValidation: () => null,
  productError: null,
  discountErrorWarnings: [],
  minimumOrderValueWarning: null,
  rewardsRef: { current: null },
  isPaying: false,
  setIsPaying: () => false,
  isTippingOpen: false,
  setIsTippingOpen: () => false,
  openTippingModal: () => null,
  createDefaultCart: () => new Promise<ConsumerCart | void>(() => {}),
  updateExceededLimitItems: () => Promise.resolve()
})

export const CheckoutContextProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const {
    cart: consumerCart,
    initConsumerCart,
    updateConsumerCart,
    updateFulfillmentConsumerCart,
    updateOrderItemQuantityConsumerCart,
    warnings: consumerCartWarnings,
    errors: consumerCartErrors
  } = useConsumerCart()
  const { partner } = useShop()
  const navigate = useNavigate()
  const { deleteQueryParam } = useQueryParams()
  const { getSessionStorageItem: getSavedCurrentLocation } =
    useSessionStorage<CurrentLocationState>('saved-current-location')
  const storeName = consumerCart?.store?.name

  const [giftWrapped, setGiftWrapped] = useState<boolean>(false)
  const [giftWrapMessage, setGiftWrapMessage] = useState<string | null>(null)
  const [timeSlotValidation, setTimeSlotValidation] =
    useState<InvalidTimeslotWarning | null>(null)
  const [dynamicPrepTimeSlotValidation, setDynamicPrepTimeSlotValidation] =
    useState<InvalidTimeslotPrepTimeWarning | null>(null)
  const [discountErrorWarnings, setDiscountErrorWarnings] = useState<
    (DiscountWarning | DiscountError)[]
  >([])
  const [productError, setProductError] = useState<CartProductError | null>(
    null
  )
  const [minimumOrderValueWarning, setMinimumOrderValueWarning] =
    useState<MinimumValueWarning | null>(null)
  const [isPaying, setIsPaying] = useState<boolean>(false)
  const [isTippingOpen, setIsTippingOpen] = useState<boolean>(false)

  // Ref used for intersection observer/scroll to rewards section on mobile when rewards are available
  const rewardsRef = useRef<HTMLDivElement>(null)

  const getGiftWrapState = () => {
    const { additionalItems } = consumerCart || {}
    const { giftWrapping } = additionalItems || {}
    const message = giftWrapping?.message || null
    return {
      giftWrapped: !!message,
      giftWrapMessage: message
    }
  }

  /** Gets & sets gift wrap state values on consumer cart load */
  useEffect(() => {
    const { giftWrapped, giftWrapMessage } = getGiftWrapState()
    setGiftWrapped(giftWrapped)
    setGiftWrapMessage(giftWrapMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCart?.id])

  /** Updates consumer cart on gift wrapping change & sets cart to show cost changes */
  const updateCartGiftWrapping = (message: string | null) => {
    // If gift wrapping is disabled, remove the message from the cart
    return updateConsumerCart({
      giftWrapMessage: message ? message : null
    })
  }

  /** Handles the opening of the Tipping Modal */
  const openTippingModal = () => {
    if (isPaying) return
    setIsTippingOpen(true)
  }

  /** Updates order item quantities to their limits when they exceed product limits */
  const updateExceededLimitItems = async (
    orderItems: OrderItemWithLimit[]
  ): Promise<void> => {
    const updatePromises = orderItems.map((item) => {
      const { quantity, limit, id } = item

      if (quantity === limit) {
        // Case A: quantity equals limit - don't update quantity
        return Promise.resolve()
      } else if (quantity > limit) {
        // Case B: quantity > limit - update quantity to the limit
        return updateOrderItemQuantityConsumerCart({
          id,
          quantity: limit
        }).catch((err) => console.error('UPDATE CART QUANTITY ERROR: ', err))
      }
      return Promise.resolve()
    })

    await Promise.all(updatePromises)
  }

  const trackAddShippingInfo = useCallback(() => {
    if (consumerCart && partner?.name && storeName) {
      trackAddShippingInfoConsumerCart(consumerCart, partner?.name, storeName)
    }
  }, [consumerCart, partner?.name, storeName])

  const handleTimeSlotUpdate = (
    warning: InvalidTimeslotWarning | OriginalFulfillmentAvailableWarning
  ) => {
    const handleUpdateSuccess = () => {
      trackTimeslotEvent(TrackableEvent.UpdateTimeslot)
      setTimeSlotValidation(null)
    }
    const handleUpdateError = () => {
      console.log('Error updating timeslot')
    }

    // Handles InvalidTimeslotWarning
    if ('nextAvailableTimeslot' in warning) {
      const { nextAvailableTimeslot } = warning
      if (!nextAvailableTimeslot) return

      updateFulfillmentConsumerCart({
        fulfillmentTime: nextAvailableTimeslot.value
      })
        .then(handleUpdateSuccess)
        .catch(handleUpdateError)
    } else {
      // Handles OriginalFulfillmentAvailableWarning
      const { fulfillmentDate, fulfillmentTime } = warning
      updateFulfillmentConsumerCart({
        fulfillmentDate,
        fulfillmentTime
      })
        .then(handleUpdateSuccess)
        .catch(handleUpdateError)
    }
  }

  /** Handles consumer cart timeslot and MOV warnings */
  useEffect(() => {
    if (!consumerCartWarnings.length) {
      if (!!timeSlotValidation) setTimeSlotValidation(null)
      if (!!dynamicPrepTimeSlotValidation)
        setDynamicPrepTimeSlotValidation(null)
      if (!!minimumOrderValueWarning) setMinimumOrderValueWarning(null)
      return
    }

    const movWarning = findErrorWarning(
      consumerCartWarnings,
      isCartErrorCode
    ) as MinimumValueWarning

    const timeslotWarning = findErrorWarning(
      consumerCartWarnings,
      isTimeSlotWarning
    ) as InvalidTimeslotWarning

    const dynamicPrepTimeSlotWarning = findErrorWarning(
      consumerCartWarnings,
      isInvalidTimeSlotPrepTimeWarning
    ) as InvalidTimeslotPrepTimeWarning

    const originalFulfillmentAvailableWarning = findErrorWarning(
      consumerCartWarnings,
      isOriginalFulfillmentAvailableWarning
    ) as OriginalFulfillmentAvailableWarning

    setDynamicPrepTimeSlotValidation(dynamicPrepTimeSlotWarning || null)

    setMinimumOrderValueWarning(movWarning || null)

    if (originalFulfillmentAvailableWarning) {
      // If the original fulfillment is available, update silently for user without showing modal
      handleTimeSlotUpdate(originalFulfillmentAvailableWarning)
      return
    }

    if (timeslotWarning) {
      const { nextAvailableTimeslot, isStoreOpen } = timeslotWarning
      // If there is a next available timeslot, update silently for user without showing modal
      if (nextAvailableTimeslot?.value && isStoreOpen) {
        handleTimeSlotUpdate(timeslotWarning)
        return
      }

      setTimeSlotValidation(timeslotWarning)
    } else setTimeSlotValidation(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCartWarnings])

  /** Handles cart product errors */
  useEffect(() => {
    if (!consumerCartErrors.length) {
      setProductError(null)
      return
    }

    const currentProductError = findErrorWarning(
      consumerCartErrors,
      isCartProductError
    ) as CartProductError

    if (currentProductError) {
      setProductError(currentProductError)
    } else setProductError(null)
  }, [consumerCartErrors])

  /** Handles consumer cart discount warnings/errors */
  useEffect(() => {
    if (!consumerCartWarnings.length && !consumerCartErrors.length) {
      // reset all validations if there are no consumerCartWarnings
      if (!!discountErrorWarnings.length) setDiscountErrorWarnings([])
      return
    }

    const currentDiscountErrorWarnings = getCurrentDiscountWarningErrors(
      consumerCartErrors,
      consumerCartWarnings
    )

    if (currentDiscountErrorWarnings.length) {
      setDiscountErrorWarnings(currentDiscountErrorWarnings)
    } else setDiscountErrorWarnings([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCartErrors, consumerCartWarnings])

  const createDefaultCart = async (
    currentPartnerStore: PartnerStore,
    queryParamFulfillmentType: FulfillmentType
  ) => {
    if (!currentPartnerStore) return

    // Find first orderFulfillment for current store which is ACCEPTING_ORDERS
    const defaultFulfillmentInfo =
      findDefaultFulfillment(
        currentPartnerStore.orderFulfillmentTypes,
        queryParamFulfillmentType
      ) ?? null

    // If there are no fulfillments ACCEPTING_ORDERS return and no cart is created
    if (!defaultFulfillmentInfo) {
      return
    }

    const fulfillmentType: FulfillmentType =
      queryParamFulfillmentType || defaultFulfillmentInfo.fulfillmentType

    // Table orders should redirect to landing page so return early,
    if (fulfillmentType === ORDER_AT_TABLE_FULFILLMENT) {
      return navigate('/')
    }

    const slerpCartParams = createDefaultCartParams(
      currentPartnerStore?.id,
      fulfillmentType,
      defaultFulfillmentInfo,
      getSavedCurrentLocation()
    )
    if (!slerpCartParams) {
      return
    }

    // Create cart using consumer API
    return initConsumerCart({ variables: slerpCartParams })
      .then((res) => {
        if (!res) {
          return
        }

        const { cart } = res || {}

        if (!cart) {
          return navigate('/')
        }
        // Delete query params to prevent them from persisting & affecting the isCartMatch value
        deleteQueryParam(['fulfillment_type', 'date', 'time'])

        return cart
      })
      .catch((err) => {
        console.error(err)
      })
  }

  return (
    <CheckoutContext.Provider
      value={{
        giftWrapped,
        setGiftWrapped,
        giftWrapMessage,
        setGiftWrapMessage,
        trackAddShippingInfo,
        updateCartGiftWrapping,
        timeSlotValidation,
        dynamicPrepTimeSlotValidation,
        setDynamicPrepTimeSlotValidation,
        productError,
        discountErrorWarnings,
        minimumOrderValueWarning,
        rewardsRef,
        isPaying,
        setIsPaying,
        isTippingOpen,
        setIsTippingOpen,
        openTippingModal,
        createDefaultCart,
        updateExceededLimitItems
      }}
    >
      {children}
    </CheckoutContext.Provider>
  )
}

export const useCheckout = () => useContext(CheckoutContext)
